import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import FilterForm from '../../../../../components/form/base/FilterForm';
import TextField from '../../../../../components/form/elements/TextField';
import {
  RADIO_COLOR,
  TEXT_FIELD_COLOR,
  TEXT_FIELD_TYPE,
  TEXT_FIELD_VARIANT,
} from '../../../../../components/form/enum/formProps';
import { ArticleFilterValues } from '../../../model/articleModel';
import * as Actions from '../../../redux/articleActions';
import MultiSelect from '../../../../../components/form/elements/MultiSelect';
import * as CommonSelectors from '../../../../../shared/redux/commonSelectors';
import * as CommonActions from '../../../../../shared/redux/commonActions';
import useDataGridProgressContext from '../../../../../components/progress/DataGridProgress/context/DataGridProgressContext';
import RadioGroup from '../../../../../components/form/elements/RadioGroup';
import Radio from '../../../../../components/form/elements/Radio';
import CategoryFilter from '../../../../../components/form/custom/CategoryFilter';
import { objectHelper } from '../../../../../shared/utils/objectHelper';
import { CategoryListGetResponse } from '../../../../category/model/categoryModel';
import { RADIO_FILTER_OPTION } from '../../../../../components/DataGrid/enums/radioFilterOption';

interface ArticleFilterProps {
  categoryId?: number;
  category?: CategoryListGetResponse;
}

const initialValues: ArticleFilterValues = {
  articleIds: '',
  title: '',
  categoryId: undefined,
  categoryOne: {
    value: '',
    key: '',
  },
  categoryTwo: {
    value: '',
    key: '',
  },
  categoryThree: {
    value: '',
    key: '',
  },
  tagsIds: [],
  keywordsIds: [],
  isPublished: RADIO_FILTER_OPTION.ALL,
};

const validationSchema = Yup.object().shape({
  articleIds: Yup.array().ensure(),
  categoryOne: Yup.object().shape({
    value: Yup.string().ensure(),
    key: Yup.string().ensure(),
  }),
  categoryTwo: Yup.object().shape({
    value: Yup.string().ensure(),
    key: Yup.string().ensure(),
  }),
  categoryThree: Yup.object().shape({
    value: Yup.string().ensure(),
    key: Yup.string().ensure(),
  }),
  tagsIds: Yup.array().ensure(),
  keywordsIds: Yup.array().ensure(),
  isPublished: Yup.number(),
});

const ArticleFilter: React.FC<ArticleFilterProps> = (props) => {
  const dispatch = useDispatch();
  const tagsList = useSelector(CommonSelectors.tagsOptionList);
  const keywordsList = useSelector(CommonSelectors.keywordsOptionList);
  const dataGridProgressContext = useDataGridProgressContext();

  useEffect(() => {
    dispatch(CommonActions.getTagsList());
  }, []);

  useEffect(() => {
    dispatch(CommonActions.getKeywordsList());
  }, []);

  return (
    <FilterForm<ArticleFilterValues>
      initialValues={{
        ...initialValues,
        categoryOne: {
          value: props.category ? props.category.name : '',
          key: props.category ? props.category.iri : '',
        },
      }}
      validationSchema={validationSchema}
      onSubmit={(values: ArticleFilterValues) => {
        dataGridProgressContext.show();
        dispatch(
          Actions.getAllArticles(
            objectHelper.removeByKeys<ArticleFilterValues>(
              ['categoryOne', 'categoryTwo', 'categoryThree'],
              objectHelper.removeEmptyValues<ArticleFilterValues>({
                ...values,
                articleIds: values.articleIds?.toString().replace('.', ','),
                isPublished:
                  values.isPublished === RADIO_FILTER_OPTION.ALL
                    ? undefined
                    : values.isPublished === RADIO_FILTER_OPTION.YES
                    ? 'true'
                    : 'false',
                categoryId: (values.categoryThree?.key.split('/').pop()
                  ? values.categoryThree?.key.split('/').pop()
                  : values.categoryTwo?.key.split('/').pop()
                  ? values.categoryTwo?.key.split('/').pop()
                  : values.categoryOne?.key.split('/').pop()) as unknown as number,
                tagsIds: (values.tagsIds as Array<string>)
                  .map((value: any) => value.split('/').pop() as string)
                  .join() as string,
                keywordsIds: (values.keywordsIds as Array<string>)
                  .map((value: any) => value.split('/').pop() as string)
                  .join() as string,
              }),
            ),
          ),
        );
      }}
      disabled={dataGridProgressContext.visibility}
    >
      <TextField
        name="articleIds"
        label="article.id"
        type={TEXT_FIELD_TYPE.NUMBER}
        color={TEXT_FIELD_COLOR.PRIMARY}
        variant={TEXT_FIELD_VARIANT.STANDARD}
      />
      <TextField
        name="title"
        label="article.title"
        type={TEXT_FIELD_TYPE.TEXT}
        color={TEXT_FIELD_COLOR.PRIMARY}
        variant={TEXT_FIELD_VARIANT.STANDARD}
      />
      <CategoryFilter readonly={true} parentId={props.categoryId} />
      <MultiSelect
        name="tagsIds"
        label="article.tags"
        choices={tagsList}
        color={TEXT_FIELD_COLOR.PRIMARY}
        variant={TEXT_FIELD_VARIANT.STANDARD}
      />
      <MultiSelect
        name="keywordsIds"
        label="article.keywords"
        choices={keywordsList}
        color={TEXT_FIELD_COLOR.PRIMARY}
        variant={TEXT_FIELD_VARIANT.STANDARD}
      />
      <RadioGroup label="article.is_published" name="isPublished">
        <Radio label="options.yes" value={RADIO_FILTER_OPTION.YES} color={RADIO_COLOR.PRIMARY} />
        <Radio label="options.no" value={RADIO_FILTER_OPTION.NO} color={RADIO_COLOR.PRIMARY} />
        <Radio label="options.all" value={RADIO_FILTER_OPTION.ALL} color={RADIO_COLOR.PRIMARY} />
      </RadioGroup>
    </FilterForm>
  );
};

export default ArticleFilter;
